import { calculateChangeTracking } from 'Data/Core/ChangeTracker';
import { RootState } from 'Data/Redux';
import { concatLatest } from 'Lib/Rxjs/operators';
import copy from 'fast-clone';
import { Epic, ofType } from 'redux-observable';
import { of } from 'rxjs';
import { filter, mergeMap } from 'rxjs/operators';
import { actions } from '.';
import config from '../../../Components/Equipment/NuclearDensityGaugeStandardisation/Config';
import webApi from '../../../Lib/WebApi';
import { addErrorNotice, callApi, camelizeObjectKeys, catchWithAuthErrors } from '../../Helpers/rxjsHelpers';
import { IDataSyncState, SyncAction } from '../DataSync/types';
import { callApiGetStandardisation, callApiPutStandardisation } from './apiCalls';

export const init: Epic = (action$) =>
  action$.pipe(
    ofType(actions.init.toString()),
    mergeMap(({ payload: { equipmentId = null } = {} }: any) =>
      callApi(
        webApi.equipment.nucleargauge.standardization.get({
          equipmentId,
        })
      ).pipe(
        mergeMap(({ Body: standardisation }) =>
          of(
            actions.initResolve({
              standardisation: camelizeObjectKeys(standardisation),
              config: config[30011],
            })
          )
        ),
        catchWithAuthErrors((err: any) =>
          of(actions.initReject(), addErrorNotice('Failed to initialise standardization.', err))
        )
      )
    )
  );

export const syncOnChanges: Epic = (action$) =>
  action$.pipe(
    ofType(
      actions.initResolve.toString(),
      actions.syncResolve.toString(),

      // Whenever a value changes
      actions.setValue.toString(),
      actions.addArrayEntry.toString(),
      actions.removeArrayEntry.toString()
    ),
    filter(({ payload: { sync = false } = {} }) => !!sync), // only when sync is set to true
    mergeMap(() => of(actions.sync()))
  );

// This is our main synchronisation epic, that is responsible for keeping the server up-to-date with the clients changes.
// This runs like a service, continually (once at a time) updating the server state to look like the client state.
export const syncData: Epic = (action$, state$) =>
  action$.pipe(
    ofType(actions.sync.toString()),
    concatLatest((action: SyncAction) => {
      const stateValue: RootState = state$.value;
      const force = action.payload?.force ?? false;
      const equipmentId = stateValue.gaugeStandardisation.equipmentId;
      const target: IDataSyncState = stateValue.gaugeStandardisation;

      const {
        syncModel: clientModelBefore,
        syncModel_synced: serverModelBefore,
        syncParams = {},
        syncParams_synced: syncedsyncParams = {},
      } = target;

      const hasChanges = calculateChangeTracking(clientModelBefore, serverModelBefore).changeCount > 0;
      const hasSyncParams = calculateChangeTracking(syncParams, syncedsyncParams).changeCount > 0;

      const shouldExitEarly = !force && !hasSyncParams && !hasChanges;
      if (shouldExitEarly) return of(actions.syncResolve());

      const serverModelSent = hasChanges ? copy(clientModelBefore) : undefined;

      const callApiPutOrGetData = (updateModel?: object) => {
        return updateModel
          ? callApiPutStandardisation({ Body: updateModel }, { equipmentId })
          : callApiGetStandardisation(equipmentId!);
      };

      return callApiPutOrGetData(serverModelSent).pipe(
        // Run the sync and update our copy of the server model to what is received
        mergeMap((serverModelReceived: any) =>
          of(
            actions.syncResolve({
              syncPatch: {
                clientModelBefore,
                serverModelSent: serverModelSent || serverModelBefore,
                serverModelReceived,
                syncParams,
              },
              sync: true,
            })
          )
        ),
        catchWithAuthErrors((error: any) => {
          return callApiPutOrGetData().pipe(
            mergeMap((serverModelReceived: any) =>
              // Repair succeeded, still report error, reset all state to server state.
              of(
                addErrorNotice('Failed to update standardization.', error),
                actions.syncResolve({
                  syncPatch: { serverModelReceived, syncParams },
                  sync: false,
                })
              )
            ),
            catchWithAuthErrors((recoveryError: any) =>
              // Repair failed, report additional error for failure and reset client state to last _known_ server state.
              of(
                addErrorNotice('Failed to update standardization.', error),
                addErrorNotice(
                  'Something went really wrong when attempting to retrieve the standardization. Try refreshing the page.',
                  recoveryError
                ),
                actions.syncResolve({ syncPatch: { syncParams }, sync: false })
              )
            )
          );
        })
      );
    })
  );
